import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from './cognito';

// Use environment variable if available, otherwise default to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Fired (on window) when any API call returns 401. AuthContext owns the single
 * auth-expiry pathway: it listens for this event, clears its state, and
 * ProtectedRoute then redirects to /login via React Router — no hard reload,
 * no second notion of "session ended".
 */
export const SESSION_EXPIRED_EVENT = 'askyourcash:session-expired';

// ── AI Feedback Types ───────────────────────────────────────────────────────

/** Thumb rating on an assistant chat message */
export type FeedbackRating = 'up' | 'down';

// ── Budget TypeScript Types ─────────────────────────────────────────────────

/** Status of spending in one budget category for a given month */
export interface BudgetCategoryStatus {
  category_name: string;
  display_name: string;
  monthly_limit: number;
  spent_this_month: number;
  remaining: number;             // negative if over budget
  percent_used: number;          // 0–100+
  status: 'on_track' | 'warning' | 'over_budget';
  transaction_count: number;
  top_merchants: string[];       // up to 3 merchant names
}

/** Full budget status response for a given month */
export interface BudgetStatusResponse {
  month: string;                          // "YYYY-MM"
  categories: BudgetCategoryStatus[];
  overall_limit: number;
  overall_spent: number;
  overall_remaining: number;
  overall_percent_used: number;
  overall_status: 'on_track' | 'warning' | 'over_budget';
  budget_count: number;
  unbudgeted_spending: number;
  unbudgeted_categories: string[];
}

/** A single saved budget category */
export interface Budget {
  id: number;
  category_name: string;
  monthly_limit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** A bank account returned by the /accounts/ endpoint */
export interface Account {
  id: number;
  account_name: string;
  account_type: string;
  balance: number;
  access_token_id: number | null;
  plaid_account_id?: string;
}

/** A Plaid connection (institution) returned by /accounts/connections */
export interface Connection {
  id: number;
  institution_name: string | null;
  institution_id: string | null;
  institution_logo: string | null;   // base64 PNG
  institution_color: string | null;  // hex color
}

/** Request body for creating or updating a budget */
export interface BudgetCreateRequest {
  category_name: string;
  monthly_limit: number;
}

// ── Goal TypeScript Types ────────────────────────────────────────────────────

/** A single saved goal */
export interface Goal {
  id: number;
  name: string;
  target_amount: number;
  monthly_contribution: number;
  plaid_account_id: string | null;
  account_name: string | null;
  start_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Status of a single goal as of a given month */
export interface GoalStatus {
  id: number;
  name: string;
  account_name: string;
  account_balance: number;
  account_goal_count: number;    // how many active goals share this account
  backed_amount: number;         // this goal's apportioned share of the account balance
  underfunded: boolean;          // backed_amount is below saved_amount
  target_amount: number;
  monthly_contribution: number;
  months_elapsed: number;
  expected_amount: number;       // auto-rising schedule target this month (informational)
  allocated_amount: number;      // == backed_amount (this goal's apportioned share)
  shortfall: number;             // backed - saved (<= 0; the unbacked "behind" amount)
  status: 'on_track' | 'behind';
  saved_amount: number;          // pot toward completion
  manual_adjustments: number;
  progress_percent: number;      // 0–100
  months_remaining: number;
  projected_achievement_date: string | null;
  is_complete: boolean;
}

/** Full goal status response for a given month */
export interface GoalStatusResponse {
  month: string;                 // "YYYY-MM"
  available_balance: number;
  goals: GoalStatus[];
  total_expected: number;
  overall_status: 'on_track' | 'behind';
  goal_count: number;
}

export class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: attach a FRESH Cognito access token. getAccessToken
    // silently refreshes via the stored refresh token when the access token has
    // expired, so the API always sees a valid token without the app tracking
    // expiry itself.
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token expiry / revocation.
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token rejected (expired, revoked, or refresh failed): notify
          // AuthContext, which clears its state so ProtectedRoute redirects to
          // /login. No-op when there was no session.
          window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        }
        return Promise.reject(error);
      }
    );
  }

  // ── Auth endpoints ──────────────────────────────────────────────────────────
  // Sign-in/up happen in Cognito (see utils/cognito.ts). The backend only warms
  // up the session, returns the profile, and revokes on logout.

  /** Warm up the session after a Cognito sign-in (Plaid refresh, Redis session). */
  async createSession() {
    return this.client.post('/auth/session');
  }

  async logout() {
    return this.client.post('/auth/logout');
  }

  async getMe() {
    return this.client.get('/auth/me');
  }

  // Protected API endpoints
  async getAccounts() {
    return this.client.get('/accounts/');
  }

  async linkPlaidAccount() {
    return this.client.post('/accounts/link-plaid');
  }

  async exchangePlaidToken(publicToken: string, metadata: any) {
    return this.client.post('/accounts/exchange-plaid-token', {
      public_token: publicToken,
      metadata: metadata,
    });
  }

  async getConnections() {
    return this.client.get('/accounts/connections');
  }

  async createUpdateLinkToken(accessTokenId: number) {
    return this.client.post('/accounts/update-link-token', { access_token_id: accessTokenId });
  }

  async refreshConnection(accessTokenId: number) {
    return this.client.post(`/accounts/connections/${accessTokenId}/refresh`);
  }

  async deleteConnection(accessTokenId: number) {
    return this.client.delete(`/accounts/connections/${accessTokenId}`);
  }

  async getTransactions() {
    return this.client.get('/transactions/');
  }

  async refreshAll() {
    // Reads access tokens from DB, repopulates Redis with accounts +
    // transactions, then budget status has real data to read from.
    // Use this on login instead of /transactions/sync which requires
    // tokens to already be in Redis.
    return this.client.post('/transactions/refresh-all');
  }

  // AI Assistant endpoints. retry=true asks the backend to hide earlier
  // attempts at this question from the LLM context (regenerate semantics).
  async queryAI(query: string, sessionId?: number, retry: boolean = false) {
    return this.client.post('/ai/query', {
      query,
      session_id: sessionId ?? null,
      retry,
    });
  }

  // Upserts the current user's rating on an assistant message. Omitting
  // comment leaves any existing comment unchanged; '' clears it.
  async submitMessageFeedback(messageId: number, rating: FeedbackRating, comment?: string) {
    const body: { rating: FeedbackRating; comment?: string } = { rating };
    if (comment !== undefined) {
      body.comment = comment;
    }
    return this.client.put(`/ai/messages/${messageId}/feedback`, body);
  }

  async getQuota() {
    return this.client.get('/ai/quota');
  }

  async getConversationHistory(sessionId?: number, limit: number = 50) {
    return this.client.get('/ai/conversation-history', {
      params: { limit, session_id: sessionId },
    });
  }

  // Chat Session endpoints
  async createChatSession(title: string = 'New Conversation') {
    return this.client.post('/ai/sessions', { title });
  }

  async listChatSessions() {
    return this.client.get('/ai/sessions');
  }

  async renameChatSession(sessionId: number, title: string) {
    return this.client.patch(`/ai/sessions/${sessionId}`, { title });
  }

  async deleteChatSession(sessionId: number) {
    return this.client.delete(`/ai/sessions/${sessionId}`);
  }

  // ── Budget endpoints ──────────────────────────────────────────────────────

  /**
   * Get all active budgets for the current user.
   * Returns list of Budget objects ordered by category name.
   */
  async getBudgets(): Promise<Budget[]> {
    const response = await this.client.get('/budgets/');
    return response.data;
  }

  async getBudgetCategories(): Promise<{ value: string; label: string }[]> {
    const res = await this.client.get('/budgets/categories');
    return res.data;
  }

  /**
   * Create a new budget or update an existing one for the same category.
   * If a budget for this category_name already exists, its limit is updated.
   */
  async createOrUpdateBudget(data: BudgetCreateRequest): Promise<Budget> {
    const response = await this.client.post('/budgets/', data);
    return response.data;
  }

  /**
   * Get the full budget status for a given month.
   * Includes per-category spending vs limits and overall totals.
   * @param month Optional "YYYY-MM" string. Defaults to current month on the server.
   */
  async getBudgetStatus(month?: string): Promise<BudgetStatusResponse> {
    const params = month ? { month } : {};
    const response = await this.client.get('/budgets/status', { params });
    return response.data;
  }

  /**
   * Partially update a budget (change limit or deactivate it).
   */
  async updateBudget(
    budgetId: number,
    data: { monthly_limit?: number; is_active?: boolean }
  ): Promise<Budget> {
    const response = await this.client.patch(`/budgets/${budgetId}`, data);
    return response.data;
  }

  /**
   * Soft-delete a budget (sets is_active=false on the server).
   * The budget no longer appears in status calculations.
   */
  async deleteBudget(budgetId: number): Promise<{ message: string; id: number }> {
    const response = await this.client.delete(`/budgets/${budgetId}`);
    return response.data;
  }

  // ── Goal endpoints ────────────────────────────────────────────────────────
  // Goals are created and funded through the AI chat (backend goal tools), so the
  // frontend only reads them. Writes go through the chat, not these methods.

  /** Get all active goals for the current user, in creation order. */
  async getGoals(): Promise<Goal[]> {
    const response = await this.client.get('/goals/');
    return response.data;
  }

  /**
   * Get the full goal status as of a given month: per-goal saved pot, how much of it
   * the funding account backs, on-track/behind status, and the achievement date.
   * @param month Optional "YYYY-MM". Defaults to current month on the server.
   */
  async getGoalStatus(month?: string): Promise<GoalStatusResponse> {
    const params = month ? { month } : {};
    const response = await this.client.get('/goals/status', { params });
    return response.data;
  }
}

const apiClientInstance = new ApiClient();
export const apiClient = apiClientInstance;
export default apiClientInstance;